import {
  useCallback,
  useRef,
  MutableRefObject,
  Dispatch,
  SetStateAction,
} from 'react';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';
import { getAudioAnalyzer, getVolume } from 'recording/services/media-visualizer-helper';

const POST_SPEECH_SILENCE_MS = 2000;
const PRE_SPEECH_SILENCE_MS = 5000;
const MAX_AUDIO_BASE64_BYTES = 5 * 1024 * 1024;
const SILENCE_THRESHOLD = 0.40;

type UseVoiceRecorderParams = {
  audioStreamRef: MutableRefObject<MediaStream | null>;
  recorderRef: MutableRefObject<RecordRTC | null>;
  setInactivityCount: Dispatch<SetStateAction<number>>;
};

type UseVoiceRecorderReturn = {
  recordLearnerTurn: (abortSignal: AbortSignal) => Promise<string | null>;
  setMicActive: (active: boolean) => void;
  initializeAnalyser: (stream: MediaStream) => void;
  cleanupRecorder: () => void;
};

const useVoiceRecorder = ({
  audioStreamRef,
  recorderRef,
  setInactivityCount,
}: UseVoiceRecorderParams): UseVoiceRecorderReturn => {
  const silenceCheckFrameRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  const stopSilenceDetection = useCallback(() => {
    if (silenceCheckFrameRef.current !== null) {
      cancelAnimationFrame(silenceCheckFrameRef.current);
      silenceCheckFrameRef.current = null;
    }
  }, []);

  const setMicActive = useCallback((active: boolean) => {
    audioStreamRef.current?.getAudioTracks().forEach((track) => {
      // eslint-disable-next-line no-param-reassign
      track.enabled = active;
    });
  }, [audioStreamRef]);

  const initializeAnalyser = useCallback((stream: MediaStream) => {
    analyserRef.current = getAudioAnalyzer(stream);
  }, []);

  const cleanupRecorder = useCallback(() => {
    stopSilenceDetection();
    analyserRef.current = null;
  }, [stopSilenceDetection]);

  // Resolves with base64-encoded WAV when speech ends, or null if no speech detected
  // within PRE_SPEECH_SILENCE_MS (triggers "are you still there?" flow).
  const recordLearnerTurn = useCallback((abortSignal: AbortSignal): Promise<string | null> => new Promise((resolve, reject) => {
    const stream = audioStreamRef.current;
    if (!stream) {
      reject(new Error('No audio stream'));
      return;
    }

    let resolveWithAudio!: () => void;

    const recorder = new RecordRTC(stream, {
      type: 'audio',
      mimeType: 'audio/wav',
      recorderType: StereoAudioRecorder,
      desiredSampRate: 16000,
      numberOfAudioChannels: 1,
      disableLogs: true,
      timeSlice: 1000,
      ondataavailable: (blob: Blob) => {
        if (Math.ceil((blob.size * 4) / 3) >= MAX_AUDIO_BASE64_BYTES) {
          resolveWithAudio();
        }
      },
    });
    recorderRef.current = recorder;
    recorder.startRecording();

    let hasSpeechDetected = false;
    let lastSpeechTime = Date.now();
    let preSpeechSilenceTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanupTimers = () => {
      stopSilenceDetection();
      if (preSpeechSilenceTimer !== null) { clearTimeout(preSpeechSilenceTimer); preSpeechSilenceTimer = null; }
    };

    resolveWithAudio = () => {
      cleanupTimers();
      recorder.stopRecording(() => {
        recorder.getDataURL((url) => {
          resolve(url.split(',')[1]);
        });
      });
    };

    const resolveWithSilence = () => {
      cleanupTimers();
      recorder.stopRecording(() => { resolve(null); });
      setInactivityCount((prevCount) => prevCount + 1);
    };

    preSpeechSilenceTimer = setTimeout(resolveWithSilence, PRE_SPEECH_SILENCE_MS);

    const checkSilence = () => {
      if (abortSignal.aborted) {
        cleanupTimers();
        recorder.stopRecording(() => {});
        reject(new DOMException('Recording aborted', 'AbortError'));
        return;
      }

      if (analyserRef.current) {
        const volume = getVolume(analyserRef.current);

        if (volume > SILENCE_THRESHOLD) {
          if (!hasSpeechDetected) {
            hasSpeechDetected = true;
            if (preSpeechSilenceTimer !== null) {
              clearTimeout(preSpeechSilenceTimer);
              preSpeechSilenceTimer = null;
            }
          }
          lastSpeechTime = Date.now();
        } else if (hasSpeechDetected && (Date.now() - lastSpeechTime) >= POST_SPEECH_SILENCE_MS) {
          resolveWithAudio();
          return;
        }
      }

      silenceCheckFrameRef.current = requestAnimationFrame(checkSilence);
    };

    silenceCheckFrameRef.current = requestAnimationFrame(checkSilence);
  }), [
    audioStreamRef,
    recorderRef,
    stopSilenceDetection,
    setInactivityCount,
  ]);

  return {
    recordLearnerTurn,
    setMicActive,
    initializeAnalyser,
    cleanupRecorder,
  };
};

export default useVoiceRecorder;
